import { createRequestHelper } from '@utils/swr'
import { create } from 'zustand'
import { type OrderStatusRequest } from '../types/apiRequest'
import { type OrderStatusResponse } from '../types/apiResponse'
import { type ButlerApis } from '../types/apis'
import { OrderStatus } from '../types/dataModel/butler'

const {
  useRequest,
  mutate: mutateOrderStatus,
  getSwrFetcher,
} = createRequestHelper<ButlerApis['get']['orderStatus'], OrderStatusResponse, OrderStatusRequest>({
  client: 'butler',
  key: params => params?.orderId ? `/orders/${params.orderId}/status` : null,
})

export const useApiOrderStatus = useRequest

interface OrderStatusStore {
  startRetryTime: Date | null
  retryCount: number
  isRetrying: boolean
  error: string | null
  retryOrderStatus: (orderId: string | null, onSuccess?: () => void, onError?: () => void) => Promise<void>
  reset: () => void
}

const MAX_RETRY_COUNT = 60
const RETRY_INTERVAL_MS = 1000

export const useOrderStatusStore = create<OrderStatusStore>()((set, get) => ({
  startRetryTime: null,
  error: null,
  retryCount: 0,
  isRetrying: false,
  retryOrderStatus: async (orderId, onSuccess, onError) => {
    const { isRetrying, error } = get()
    if (!orderId || isRetrying || error) return
    set({ startRetryTime: new Date(), retryCount: 0, isRetrying: true })
    const scheduleRetry = (onExhausted: () => void) => {
      const retryCount = get().retryCount
      if (retryCount >= MAX_RETRY_COUNT) {
        onExhausted()
        return
      }
      setTimeout(() => {
        fetchOrderStatus()
        set({ retryCount: retryCount + 1 })
      }, RETRY_INTERVAL_MS)
    }
    const fetchOrderStatus = async () => {
      const swrFetcher = getSwrFetcher()
      let res: Awaited<ReturnType<typeof swrFetcher>>
      try {
        res = await swrFetcher(`/orders/${orderId}/status`)
      }
      catch (fetchError) {
        // 建單後前幾秒的 status 查詢偶爾會回沒帶 CORS header 的錯誤；不接住會讓 isRetrying 卡在 true 永遠停在辨識中，
        // 直接判定失敗又會把暫時性錯誤放大成錯誤頁，所以和 in_progress 一樣消耗重試次數後再試
        scheduleRetry(() => {
          set({ isRetrying: false, error: fetchError instanceof Error ? fetchError.message : 'Fetch order status failed' })
          onError?.()
        })
        return
      }
      mutateOrderStatus({
        orderId,
      }, res, {
        revalidate: false,
      })
      if (res?.data.data?.status === OrderStatus.IN_PROGRESS) {
        scheduleRetry(() => {
          set({ isRetrying: false, error: 'Max retry count reached' })
          onError?.()
        })
      }
      else {
        set({ isRetrying: false })
        onSuccess?.()
      }
    }
    fetchOrderStatus()
  },
  reset: () => {
    set({ startRetryTime: null, retryCount: 0, isRetrying: false, error: null })
  },
}))
